import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, message, Spin, Typography, App, Tooltip, Drawer, Flex, Grid } from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchPackingList, deletePacking } from "./packingThunks";
import {
  setIsEditPacking,
  setSelectedPacking,
  clearMessages,
} from "./packingSlice";
import { useNavigate } from "react-router-dom";
import { getAccessType } from "../../../lib/functions";

const PackingStructureList = () => {
  const { Title } = Typography;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [access_type, access_type_set] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const access = getAccessType("item_entry");
    access_type_set(access);
  }, []);

  const { packDetails, loadingList, successMessage, error } = useSelector(
    (state) => state.packing
  );
  useEffect(() => {
    dispatch(fetchPackingList());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) message.success(successMessage);
    if (error) message.error(error);
    return () => dispatch(clearMessages());
  }, [successMessage, error, dispatch]);
  const { modal } = App.useApp();
const handleDelete = (id) => {
  const pkg = packDetails?.find(p => p.item_id === id);

  modal.confirm({
    title: "Are you sure?",
    content: `This will delete package: ${pkg?.itemName || "Unknown Item"}`,
    okText: "Yes, Delete",
    cancelText: "Cancel",
    onOk: async () => {
      try {
        await dispatch(deletePacking(id)).unwrap();
        modal.success({ title: "Package deleted successfully" });
        dispatch(fetchPackingList());
      } catch (err) {
        console.log('Error deleting packing:', err)
        modal.error({ title: err.message || "Delete failed" });
      }
    },
  });
};


  // 🔹 Base column
  let dynamicColumns = [
    { title: "NO.", render: (_, __, index) => index + 1 },
    { title: "Item Name", dataIndex: "itemName", key: "itemName" },
  ];

  // 🔹 Define all possible pack fields
  const packFields = [
    { key: "std", label: "Standard" },
    { key: "second", label: "Second" },
    { key: "third", label: "Third" },
    { key: "fourth", label: "Fourth" },
    { key: "fifth", label: "Fifth" },
    { key: "sixth", label: "Sixth" },
  ];

  // 🔹 Only add columns if pack exists in at least one record
  packFields.forEach((p) => {
    const hasPack = packDetails?.some(
      (row) => row[`${p.key}PackName`] && row[`${p.key}PackName`] !== ""
    );
    if (hasPack) {
      dynamicColumns.push(
        {
          title: `${p.label} Pack Name`,
          dataIndex: `${p.key}PackName`,
          key: `${p.key}PackName`,
        },
        {
          title: `${p.label} Pack Quantity`,
          dataIndex: `${p.key}PackQty`,
          key: `${p.key}PackQty`,
        },
        {
          title: `${p.label} Pack Rate`,
          dataIndex: `${p.key}Rate`,
          key: `${p.key}Rate`,
        }
      );
    }
  });

  // 🔹 Add actions column
 
    access_type == "WRITE" &&
      dynamicColumns.push({
        title: "Actions",
        render: (_, record) => (
          <>
            <Button
              type="link"
              onClick={() => {
                dispatch(setSelectedPacking(record));
                dispatch(setIsEditPacking(true));
                navigate("addPacking");
              }}
            >
              Edit
            </Button>
            <Button danger onClick={() => handleDelete(record.item_id)}>
              Delete
            </Button>
          </>
        ),
      });
 



  return (
    <Spin spinning={loadingList}>
      <Row justify="end" style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12}>
          <Title
            level={4} // 👈 smaller font size (change to 3 or 5 as needed)
            style={{
              margin: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Packing Structure List
          </Title>
        </Col>
        <Col
          xs={24}
          sm={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Flex align="center" gap={6}>
            <Tooltip title="Help">
              <Button
                type="text"
                icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
                onClick={() => setHelpOpen(true)}
              />
            </Tooltip>

            {access_type == "WRITE" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedPacking(null));
                  dispatch(setIsEditPacking(false));
                  navigate("/inventory/product/addPacking");
                }}
              >
                Add New
              </Button>
            )}
          </Flex>
        </Col>
      </Row>

      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Table
          bordered
          columns={dynamicColumns}
          dataSource={packDetails}
          rowKey="item_id"
          loading={loadingList}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          size="small"
        />
      </div>
      <Drawer
        title="Packing Structure Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            Packing Structure Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px", marginLeft:17 }}>
            This section helps manage item packing structures, packing quantities,
            rates, and multi-level packaging details used in inventory and sales workflows.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Create packing structures for inventory items</li>
              <li>Manage multiple packaging levels for products</li>
              <li>Configure packing quantities and conversion values</li>
              <li>Set packing rates for different package types</li>
              <li>View standard and additional packing levels</li>
              <li>Edit packing details and item packaging structures</li>
              <li>Delete packing structures when required</li>
              <li>Track package hierarchy and quantity breakdowns</li>
              <li>Manage inventory packaging workflows efficiently</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Items can contain multiple packaging levels.</li>
              <li>Packing quantities affect inventory calculations.</li>
              <li>Rates can vary based on package type and quantity.</li>
              <li>Packaging structures help simplify stock management.</li>
              <li>Only available packaging levels are displayed in the table.</li>
              <li>Changes in packing structures affect inventory workflows.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </Spin>
  );
};

export default PackingStructureList;
